import { createElement, lazy } from 'react';
import type { RouteObject } from 'react-router';

import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';

const CampaignsPage = lazy(() =>
  import('./pages/CampaignsPage').then((module) => ({ default: module.CampaignsPage })),
);
const CampaignDetailPage = lazy(() =>
  import('./pages/CampaignDetailPage').then((module) => ({ default: module.CampaignDetailPage })),
);
const NewCampaignPage = lazy(() =>
  import('./pages/NewCampaignPage').then((module) => ({ default: module.NewCampaignPage })),
);
const MyCampaignsPage = lazy(() =>
  import('./pages/MyCampaignsPage').then((module) => ({ default: module.MyCampaignsPage })),
);
const ExamPage = lazy(() =>
  import('./pages/ExamPage').then((module) => ({ default: module.ExamPage })),
);
const ResultPage = lazy(() =>
  import('./pages/ResultPage').then((module) => ({ default: module.ResultPage })),
);
const CampaignTrackingListPage = lazy(() =>
  import('./pages/CampaignTrackingListPage').then((module) => ({
    default: module.CampaignTrackingListPage,
  })),
);
const CampaignTrackingDetailPage = lazy(() =>
  import('./pages/CampaignTrackingDetailPage').then((module) => ({
    default: module.CampaignTrackingDetailPage,
  })),
);
const CampaignManualWorkersPage = lazy(() =>
  import('./pages/CampaignManualWorkersPage').then((module) => ({
    default: module.CampaignManualWorkersPage,
  })),
);

const OralCampaignsPage = lazy(() =>
  import('./pages/OralCampaignsPage').then((module) => ({ default: module.OralCampaignsPage })),
);
const OralCandidatesPage = lazy(() =>
  import('./pages/OralCandidatesPage').then((module) => ({ default: module.OralCandidatesPage })),
);
const OralExamPage = lazy(() =>
  import('./pages/OralExamPage').then((module) => ({ default: module.OralExamPage })),
);

export const evaluationsRoutes: RouteObject[] = [
  {
    path: 'evaluacion/campanas',
    children: [
      {
        element: createElement(ProtectedRoute, { view: 'campaigns' }),
        children: [
          { index: true, element: createElement(CampaignsPage) },
          { path: ':id', element: createElement(CampaignDetailPage) },
        ],
      },
      {
        path: 'nueva',
        element: createElement(ProtectedRoute, { view: 'newCampaign' }),
        children: [{ index: true, element: createElement(NewCampaignPage) }],
      },
    ],
  },
  // Employee-facing routes
  {
    path: 'evaluacion/mis-campanas',
    element: createElement(ProtectedRoute, { view: 'myCampaigns' }),
    children: [{ index: true, element: createElement(MyCampaignsPage) }],
  },
  {
    // The exam is the worker's own flow, not the campaign admin's: a plain
    // EMPLOYEE holds `my_campaigns`, not `view_campaign`. Gating it with
    // `campaigns` bounced the worker out to Forbidden (backend OR tuple).
    path: 'evaluacion/examen/:evaluationId',
    element: createElement(ProtectedRoute, { view: 'myCampaigns' }),
    children: [{ index: true, element: createElement(ExamPage) }],
  },
  {
    path: 'evaluacion/resultado/:attemptId',
    element: createElement(ProtectedRoute, { view: 'myCampaigns' }),
    children: [{ index: true, element: createElement(ResultPage) }],
  },
  // Reevaluaciones (oral): campañas → trabajadores desaprobados → evaluación
  {
    path: 'evaluacion/reevaluaciones',
    element: createElement(ProtectedRoute, { view: 'oralEvaluations' }),
    children: [
      { index: true, element: createElement(OralCampaignsPage) },
      { path: ':campaignId', element: createElement(OralCandidatesPage) },
      { path: ':campaignId/:evaluationId', element: createElement(OralExamPage) },
    ],
  },
  // Campaign tracking (read-only dashboard: campañas → manuales → trabajadores)
  {
    path: 'evaluacion/seguimiento',
    children: [
      {
        element: createElement(ProtectedRoute, { view: 'campaignTracking' }),
        children: [
          { index: true, element: createElement(CampaignTrackingListPage) },
          { path: ':id', element: createElement(CampaignTrackingDetailPage) },
          { path: ':id/manuales/:manualId', element: createElement(CampaignManualWorkersPage) },
        ],
      },
    ],
  },
];
